import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { trackLeadConversion } from "../../lib/gtag";
import { Reveal, Eyebrow } from "./Reveal";
import { ArrowRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-md border border-white/[0.1] bg-[#0B0D10] px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition-colors duration-200 focus:border-[#F25F22]/60 focus:ring-2 focus:ring-[#F25F22]/20";

const initial = {
  name: "",
  company: "",
  email: "",
  phone: "",
  fleet_size: "1–3 trucks",
  crm: "",
  preferred_date: "",
  preferred_window: "Morning (8–12)",
};

export default function LeadForm({ source = "hvac" }) {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, source });
      trackLeadConversion();
      toast.success("Request received", {
        description:
          "We'll reach out within one business day to schedule your demo.",
      });
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong", {
        description: "Please check your details and try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="demo" data-testid="lead-form-section" className="relative border-t border-white/[0.06] bg-[#0E1117] py-24 lg:py-32">
      <div className="absolute left-1/2 top-0 h-[400px] w-[600px] -translate-x-1/2 rounded-full bg-[#F25F22]/[0.08] blur-[140px]" />
      <div className="relative mx-auto grid max-w-6xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-2">
        <Reveal>
          <Eyebrow>Prefer a Human?</Eyebrow>
          <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl lg:text-4xl">
            See your own missed calls turn into bookings
          </h2>
          <p className="mt-4 max-w-md text-base leading-relaxed text-slate-400">
            We&apos;ll pull a sample of your call logs, show you exactly what
            slipped through, and walk you through a live recovery — in 20
            minutes, on your numbers.
          </p>
          <p className="mt-4 text-sm text-slate-500">
            No demo needed —{" "}
            <a
              href="/signup"
              data-testid="leadform-signup-link"
              className="font-semibold text-[#F25F22] underline-offset-4 transition-colors hover:text-[#FF7539] hover:underline"
            >
              sign up directly
            </a>{" "}
            and you&apos;re live within 48 hours.
          </p>
          <ul className="mt-8 space-y-3 text-sm text-slate-300">
            {[
              "Live within 48 hours of signup",
              "No dispatcher hire, no new phone system",
              "90-day revenue guarantee on Growth",
            ].map((t) => (
              <li key={t} className="flex items-center gap-3">
                <span className="h-1.5 w-1.5 rounded-full bg-[#F25F22]" />
                {t}
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal delay={0.12}>
          <form
            onSubmit={submit}
            data-testid="demo-lead-form"
            className="rounded-xl border border-white/[0.1] bg-[#12161F]/80 p-7 shadow-2xl backdrop-blur-xl sm:p-9"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-1">
                <label htmlFor="lead-name" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Full name
                </label>
                <input
                  id="lead-name"
                  data-testid="lead-name-input"
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Mike Torrance"
                  className={inputCls}
                />
              </div>
              <div className="sm:col-span-1">
                <label htmlFor="lead-company" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Company
                </label>
                <input
                  id="lead-company"
                  data-testid="lead-company-input"
                  required
                  value={form.company}
                  onChange={set("company")}
                  placeholder="Apex Heating & Air"
                  className={inputCls}
                />
              </div>
              <div className="sm:col-span-1">
                <label htmlFor="lead-email" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Work email
                </label>
                <input
                  id="lead-email"
                  data-testid="lead-email-input"
                  type="email"
                  required
                  value={form.email}
                  onChange={set("email")}
                  placeholder="mike@apexhvac.com"
                  className={inputCls}
                />
              </div>
              <div className="sm:col-span-1">
                <label htmlFor="lead-phone" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Phone
                </label>
                <input
                  id="lead-phone"
                  data-testid="lead-phone-input"
                  type="tel"
                  required
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="(555) 014-2231"
                  className={inputCls}
                />
              </div>
              <div className="sm:col-span-1">
                <label htmlFor="lead-fleet" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Fleet size
                </label>
                <select
                  id="lead-fleet"
                  data-testid="lead-fleet-select"
                  value={form.fleet_size}
                  onChange={set("fleet_size")}
                  className={inputCls}
                >
                  {["1–3 trucks", "4–12 trucks", "13+ trucks"].map((o) => (
                    <option key={o}>{o}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-1">
                <label htmlFor="lead-crm" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Current CRM
                </label>
                <select
                  id="lead-crm"
                  data-testid="lead-crm-select"
                  value={form.crm}
                  onChange={set("crm")}
                  className={inputCls}
                >
                  <option value="" disabled>
                    Select your CRM…
                  </option>
                  {["ServiceTitan", "Housecall Pro", "Jobber", "FieldEdge", "None yet"].map((o) => (
                    <option key={o}>{o}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-1">
                <label htmlFor="lead-date" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Preferred demo date
                </label>
                <input
                  id="lead-date"
                  data-testid="lead-date-input"
                  type="date"
                  min={new Date().toISOString().split("T")[0]}
                  value={form.preferred_date}
                  onChange={set("preferred_date")}
                  className={`${inputCls} [color-scheme:dark]`}
                />
              </div>
              <div className="sm:col-span-1">
                <label htmlFor="lead-window" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Time window
                </label>
                <select
                  id="lead-window"
                  data-testid="lead-window-select"
                  value={form.preferred_window}
                  onChange={set("preferred_window")}
                  className={inputCls}
                >
                  {["Morning (8–12)", "Afternoon (12–4)", "Evening (4–7)"].map((o) => (
                    <option key={o}>{o}</option>
                  ))}
                </select>
              </div>
            </div>

            <button
              type="submit"
              data-testid="lead-submit-button"
              disabled={loading}
              className="group mt-6 flex w-full items-center justify-center gap-2 rounded-md bg-[#F25F22] py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-[#FF7539] hover:shadow-[0_0_28px_rgba(242,95,34,0.5)] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  Book My Demo
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                </>
              )}
            </button>
            <p className="mt-4 text-center text-xs text-slate-500">
              No spam, no robocalls. A real human follows up.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
